import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { usePermissions, useTranslate } from "react-admin";
import { Navigate } from "react-router-dom";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { AnalyticsFilterBar } from "../analytics/AnalyticsFilterBar";
import { useAnalyticsData } from "../analytics/AnalyticsProvider";
import { useAnalyticsFilters } from "../analytics/useAnalyticsFilters";
import {
  AnalyticsGroupBy,
  CHART_PALETTE,
  DailyRow,
  formatAnalyticsGroupKey,
  formatDateLabel,
  generateKeys,
  getAnalyticsGroupKey,
  Granularity,
  KpiKey,
  truncDate,
} from "../analytics/utils";
import { ResourceName } from "../constants/resources";
import { color, space } from "../theme/designTokens";

const ALL_KEY = "__all__";

const PERCENTAGE_FORMAT = new Intl.NumberFormat("de-DE", {
  style: "percent",
  maximumFractionDigits: 1,
  signDisplay: "exceptZero",
});

type ChartDataPoint = Record<string, string | number>;

type TooltipEntry = { dataKey: string; name: string; value: number; color: string };

const ChartTooltip = ({
  active,
  payload,
  label,
  granularity,
  data,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
  granularity: Granularity;
  data: ChartDataPoint[];
}) => {
  const t = useTranslate();
  if (!active || !payload?.length || !label) return null;

  const currentIdx = data.findIndex(d => d.date === label);
  const prevPoint = currentIdx > 0 ? data[currentIdx - 1] : null;

  return (
    <Paper variant="outlined" sx={{ p: 0, minWidth: 200, overflow: "hidden", bgcolor: "background.paper" }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 1.5, pt: 1, pb: 0.5 }}>
        {formatDateLabel(String(label), granularity)}
      </Typography>
      {payload.map(entry => {
        const val = entry.value ?? 0;
        const prevVal = prevPoint ? (prevPoint[entry.dataKey] as number) : null;
        const delta = prevVal && prevVal > 0 ? (val - prevVal) / prevVal : null;
        const deltaColor = delta === null || delta === 0 ? color.gray : delta > 0 ? color.success : color.error;

        return (
          <Box
            key={entry.dataKey}
            sx={{ display: "flex", alignItems: "stretch", borderLeft: `3px solid ${entry.color}`, mb: 0.25 }}
          >
            <Box sx={{ px: 1.5, py: 0.75, flex: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                {entry.name}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {val.toLocaleString("de-DE")}
              </Typography>
              {delta !== null && (
                <Typography variant="caption" sx={{ color: deltaColor, fontWeight: 600 }}>
                  {PERCENTAGE_FORMAT.format(delta)} {t(GRANULARITY_PREV_KEY[granularity])}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Paper>
  );
};

const GRANULARITY_PREV_KEY: Record<Granularity, string> = {
  [Granularity.Day]: "analytics.fromPrevDay",
  [Granularity.Month]: "analytics.fromPrevMonth",
  [Granularity.Year]: "analytics.fromPrevYear",
};

type KpiCardProps = {
  title: string;
  value: number;
  delta: number | null;
  suffix?: string;
};

const KpiCard = ({ title, value, delta, suffix }: KpiCardProps) => {
  const DeltaIcon = delta === null || delta === 0 ? TrendingFlatIcon : delta > 0 ? TrendingUpIcon : TrendingDownIcon;
  const deltaColor = delta === null || delta === 0 ? color.gray : delta > 0 ? color.success : color.error;

  return (
    <Paper sx={{ p: 2, minWidth: 160, flex: 1 }} variant="outlined">
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 600, mt: 0.5 }}>
        {suffix ? `${value.toLocaleString("de-DE")}${suffix}` : value.toLocaleString("de-DE")}
      </Typography>
      {delta !== null && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
          <DeltaIcon sx={{ fontSize: 16, color: deltaColor }} />
          <Typography variant="body2" sx={{ color: deltaColor }}>
            {PERCENTAGE_FORMAT.format(delta)}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const computeKpiSummary = (rows: DailyRow[]) => {
  if (rows.length === 0) return null;

  const dates = Array.from(new Set(rows.map(r => r.date.slice(0, 10)))).sort();
  const latestDate = dates[dates.length - 1];
  const prevDate = dates.length >= 2 ? dates[dates.length - 2] : null;

  const sumForDate = (date: string, metric: KpiKey) =>
    rows.filter(r => r.date.slice(0, 10) === date).reduce((sum, r) => sum + r[metric], 0);

  const dau = sumForDate(latestDate, "dau");
  const mau = sumForDate(latestDate, "mau");
  const stickiness = mau > 0 ? dau / mau : 0;

  const prevDau = prevDate ? sumForDate(prevDate, "dau") : null;
  const prevMau = prevDate ? sumForDate(prevDate, "mau") : null;
  const prevStickiness = prevMau && prevMau > 0 ? (prevDau ?? 0) / prevMau : null;

  const dauDelta = prevDau && prevDau > 0 ? (dau - prevDau) / prevDau : null;
  const mauDelta = prevMau && prevMau > 0 ? (mau - prevMau) / prevMau : null;
  const stickinessDelta = prevStickiness && prevStickiness > 0 ? (stickiness - prevStickiness) / prevStickiness : null;

  return { dau, mau, stickiness, dauDelta, mauDelta, stickinessDelta, latestDate, prevDate };
};

const TrendChart = ({
  rows,
  groupBy,
  groupKeys,
  metric,
  label,
  dateFrom,
  dateTo,
  granularity,
  showTotal,
}: {
  rows: DailyRow[];
  groupBy: AnalyticsGroupBy;
  groupKeys: string[];
  metric: KpiKey;
  label: string;
  dateFrom: string;
  dateTo: string;
  granularity: Granularity;
  showTotal: boolean;
}) => {
  const data = useMemo(() => {
    type Acc = { sum: number; count: number };
    const byKey = new Map<string, Record<string, Acc>>();
    for (const r of rows) {
      const key = truncDate(r.date, granularity);
      const entry = byKey.get(key) ?? {};
      const groupKey = getAnalyticsGroupKey(r.appId, groupBy);
      const prev = entry[groupKey] ?? { sum: 0, count: 0 };
      entry[groupKey] = { sum: prev.sum + r[metric], count: prev.count + 1 };
      byKey.set(key, entry);
    }
    return generateKeys(dateFrom, dateTo, granularity).map(date => {
      const values = byKey.get(date) ?? {};
      const point: Record<string, string | number> = {
        date,
        ...Object.fromEntries(Object.entries(values).map(([f, { sum, count }]) => [f, Math.round(sum / count)])),
      };
      if (showTotal) {
        point[ALL_KEY] = Object.values(values).reduce((sum, { sum: s, count: c }) => sum + Math.round(s / c), 0);
      }
      return point;
    });
  }, [rows, metric, granularity, dateFrom, dateTo, groupBy, showTotal]);

  const allGroupKeys = useMemo(() => (showTotal ? [ALL_KEY, ...groupKeys] : groupKeys), [groupKeys, showTotal]);

  const [hoveredFlavour, setHoveredFlavour] = useState<string | null>(null);
  const handleMouseLeave = useCallback(() => setHoveredFlavour(null), []);
  const t = useTranslate();

  return (
    <Box sx={{ mb: space.lg }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 8, right: 24, bottom: 8, left: 0 }} onMouseLeave={handleMouseLeave}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={v => formatDateLabel(v, granularity)} />
          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
          <Tooltip
            content={props => (
              <ChartTooltip
                active={props.active}
                payload={
                  hoveredFlavour
                    ? (props.payload as unknown as TooltipEntry[] | undefined)?.filter(
                        e => e.dataKey === hoveredFlavour
                      )
                    : (props.payload as unknown as TooltipEntry[] | undefined)
                }
                label={props.label as string | undefined}
                granularity={granularity}
                data={data}
              />
            )}
          />
          <Legend onMouseEnter={e => setHoveredFlavour(e.dataKey as string)} onMouseLeave={handleMouseLeave} />
          {allGroupKeys.map((f, i) => (
            <Line
              key={f}
              type="monotone"
              dataKey={f}
              name={
                f === ALL_KEY
                  ? t(groupBy === AnalyticsGroupBy.AppId ? "analytics.allApps" : "analytics.allFlavours")
                  : formatAnalyticsGroupKey(f, groupBy)
              }
              stroke={f === ALL_KEY ? color.gray : CHART_PALETTE[i % CHART_PALETTE.length]}
              strokeWidth={hoveredFlavour === f ? 3 : 2}
              strokeOpacity={hoveredFlavour === null || hoveredFlavour === f ? 1 : 0.15}
              strokeDasharray={f === ALL_KEY ? "6 3" : undefined}
              dot={false}
              isAnimationActive={false}
              onMouseEnter={() => setHoveredFlavour(f)}
              onMouseLeave={handleMouseLeave}
              style={{ cursor: "pointer" }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const AnalyticsDashboard = () => {
  const { permissions, isPending } = usePermissions();
  const t = useTranslate();
  const { dailyRows, loading, error } = useAnalyticsData();
  const filters = useAnalyticsFilters();
  const [groupBy, setGroupBy] = useState<AnalyticsGroupBy>(AnalyticsGroupBy.AppId);
  const [showTotal, setShowTotal] = useState(false);

  const filteredRows = useMemo(
    () =>
      dailyRows
        .filter(r => r.date.slice(0, 10) >= filters.deferredFrom && r.date.slice(0, 10) <= filters.deferredTo)
        .sort((a, b) => b.date.localeCompare(a.date) || a.appId.localeCompare(b.appId)),
    [dailyRows, filters.deferredFrom, filters.deferredTo]
  );

  const latestDate = filteredRows[0]?.date;
  const chartGroupKeys = useMemo(
    () => Array.from(new Set(filteredRows.map(row => getAnalyticsGroupKey(row.appId, groupBy)))).sort(),
    [filteredRows, groupBy]
  );
  const kpi = useMemo(() => computeKpiSummary(filteredRows), [filteredRows]);

  if (!isPending && !permissions?.includes(ResourceName.Analytics)) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ p: 4 }}>
      <AnalyticsFilterBar
        interval={filters.interval}
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        granularity={filters.granularity}
        latestDate={latestDate}
        onIntervalChange={filters.handleIntervalChange}
        onDateFromChange={filters.setDateFrom}
        onDateToChange={filters.setDateTo}
        onGranularityChange={filters.setGranularity}
      />
      <FormControl size="small" sx={{ minWidth: 180, mt: space.sm, mb: space.lg }}>
        <InputLabel id="analytics-dashboard-group-by-label">{t("analytics.groupBy")}</InputLabel>
        <Select
          labelId="analytics-dashboard-group-by-label"
          value={groupBy}
          label={t("analytics.groupBy")}
          onChange={(event: SelectChangeEvent<AnalyticsGroupBy>) => setGroupBy(event.target.value as AnalyticsGroupBy)}
        >
          <MenuItem value={AnalyticsGroupBy.AppId}>{t("analytics.groupByOption.appId")}</MenuItem>
          <MenuItem value={AnalyticsGroupBy.Flavour}>{t("analytics.groupByOption.flavour")}</MenuItem>
        </Select>
      </FormControl>

      {loading && <CircularProgress sx={{ my: space.lg }} />}
      {error && (
        <Typography color="error" sx={{ mb: space.md }}>
          {t("analytics.loadError")}
        </Typography>
      )}

      {kpi && (
        <Box sx={{ mb: space.lg }}>
          <Box sx={{ display: "flex", gap: space.sm, flexWrap: "wrap" }}>
            <KpiCard title={t("analytics.kpi.dau")} value={kpi.dau} delta={kpi.dauDelta} />
            <KpiCard title={t("analytics.kpi.mau")} value={kpi.mau} delta={kpi.mauDelta} />
            <KpiCard
              title={t("analytics.kpi.stickiness")}
              value={Math.round(kpi.stickiness * 100)}
              delta={kpi.stickinessDelta}
              suffix="%"
            />
          </Box>
          {kpi.prevDate && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              {t("analytics.comparedTo", { date: kpi.prevDate })}
            </Typography>
          )}
        </Box>
      )}

      {filters.deferredFrom && filters.deferredTo && (
        <>
          <FormControlLabel
            control={<Checkbox checked={showTotal} onChange={(_, v) => setShowTotal(v)} size="small" />}
            label={t(groupBy === AnalyticsGroupBy.AppId ? "analytics.allApps" : "analytics.allFlavours")}
            sx={{ mb: space.sm }}
          />
          <TrendChart
            rows={filteredRows}
            groupBy={groupBy}
            groupKeys={chartGroupKeys}
            metric="dau"
            label={t("analytics.kpi.dau")}
            dateFrom={filters.deferredFrom}
            dateTo={filters.deferredTo}
            granularity={filters.granularity}
            showTotal={showTotal}
          />
          <TrendChart
            rows={filteredRows}
            groupBy={groupBy}
            groupKeys={chartGroupKeys}
            metric="mau"
            label={t("analytics.kpi.mau")}
            dateFrom={filters.deferredFrom}
            dateTo={filters.deferredTo}
            granularity={filters.granularity}
            showTotal={showTotal}
          />
        </>
      )}
    </Box>
  );
};
